/**
 * TopBar — floating sticky header.
 *
 * Visual language updates for the bento redesign:
 *   • Translucent surface with backdrop-blur(20px) saturate(180%)
 *     so the gradient body bg shows through faintly while keeping
 *     legibility on the page title.
 *   • Search button is now a pill with the brand-blue ⌘K chip — the
 *     CommandBar / palette is Lenavio's primary interaction and this
 *     control should look unmistakably tappable from across the screen.
 *   • Notification bell keeps its red dot but gains a soft red glow
 *     so it reads from peripheral vision.
 *   • Theme toggle, language, profile chrome unchanged in behaviour
 *     but all use the same rounded-xl pill shape.
 */
import { useLocation } from "react-router-dom";
import { Menu, Moon, Search, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/hooks/useTheme";
import { navRoutes } from "@/lib/routes";
import { NotificationBell } from "./NotificationBell";
import { LanguageSwitcher } from "./LanguageSwitcher";

interface TopBarProps {
  onSearchOpen: () => void;
  /**
   * Renders a hamburger button on the left edge when provided. The
   * shell passes this only on mobile so the user can open the drawer
   * sidebar without needing to first click a near-invisible chevron.
   */
  onMenuOpen?: () => void;
}

export function TopBar({ onSearchOpen, onMenuOpen }: TopBarProps) {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const { t } = useTranslation();

  const currentRoute = navRoutes.find((r) =>
    r.path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(r.path),
  );
  const pageTitle = currentRoute
    ? t(currentRoute.labelKey, { defaultValue: currentRoute.label })
    : "Lenavio";

  return (
    <header
      className="sticky top-0 z-20 h-16 border-b border-border/60"
      style={{
        // Translucent so the body gradient shows through. The blur
        // is the same recipe as the dashboard cards' backdrop —
        // keeps everything in the same visual family.
        background: "rgb(var(--color-bg) / 0.7)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
      }}
    >
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Page title row */}
        <div className="flex items-center gap-2 min-w-0">
          {onMenuOpen && (
            <button
              type="button"
              onClick={onMenuOpen}
              aria-label="Open navigation menu"
              className="md:hidden p-2 -ml-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <h1
            className="text-[18px] font-extrabold font-heading text-text-primary truncate"
            style={{ letterSpacing: "-0.01em" }}
          >
            {pageTitle}
          </h1>
        </div>

        {/* Right-side actions */}
        <div className="flex items-center gap-2">
          {/* Search pill — Lenavio's primary surface for natural-language
              questions. The visual weight (gradient kbd, brand-blue
              accent on hover) deliberately telegraphs "this is THE thing
              you click". */}
          <button
            type="button"
            onClick={onSearchOpen}
            aria-label="Open command palette (Cmd+K)"
            aria-keyshortcuts="Meta+K Control+K"
            className="group flex items-center gap-2.5 px-4 py-2 rounded-xl border border-border bg-surface/80 text-sm font-medium text-text-muted hover:text-text-primary hover:border-accent/40 hover:shadow-[0_2px_10px_rgba(27,24,255,0.08)] transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Search
              className="h-4 w-4 group-hover:text-accent transition-colors"
              aria-hidden="true"
            />
            <span className="hidden sm:inline">{t("common.search")}</span>
            <kbd
              aria-hidden="true"
              className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[11px] font-bold bg-surface-muted border border-border text-text-muted"
            >
              <span className="text-xs">&#8984;</span>K
            </kbd>
          </button>

          {/* Language */}
          <LanguageSwitcher />

          {/* Notifications */}
          <NotificationBell />

          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={
              theme === "light" ? "Switch to dark mode" : "Switch to light mode"
            }
            aria-pressed={theme === "dark"}
            className="w-[38px] h-[38px] rounded-xl flex items-center justify-center text-text-muted hover:text-text-primary border border-border bg-surface/80 hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          >
            {theme === "light" ? (
              <Moon className="h-[17px] w-[17px]" aria-hidden="true" />
            ) : (
              <Sun className="h-[17px] w-[17px]" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
